"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useReducer } from "react";
import { fileUploaderReducer, initialFileUploaderState } from "../reducers/fileUploadReducer";
import { validateFile, testVideoCompatibility } from "../utils/fileValidation";
import { PROCESSING_TIMEOUT_MS } from "../utils/constants";
import type { FileUploaderState } from "../reducers/types";

/**
 * Interface for the return value of the useFileUploader hook
 */
interface FileUploaderHook {
  /** Current state of the file uploader */
  state: FileUploaderState;
  /** Process a file for upload */
  processFile: (file: File) => void;
  /** Retry processing the current file */
  handleRetry: () => void;
  /** Reset the file uploader state */
  resetState: () => void;
  /** Handle file removal */
  handleRemoveFile: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Direct access to the dispatch function for custom actions */
  dispatch: React.Dispatch<import("../reducers/types").FileUploaderAction>;
}

/**
 * Props for the useFileUploader hook
 */
interface UseFileUploaderProps {
  /** Callback to be called when a file is selected */
  onFileSelect: (file: File) => void;
  /** Callback to be called when a file is uploaded */
  setUploaded: (uploaded: boolean) => void;
  /** Callback to show toast notifications */
  showToast?: (toast: {
    type: any;
    message: string | React.ReactNode;
  }) => void;
}

/**
 * Hook for managing file upload state and logic
 *
 * Handles file processing, validation, compatibility checking,
 * error handling, and state management.
 *
 * @param props Hook props
 * @returns Object with state and handlers
 */
const useFileUploader = t0 => {
  const $ = _c(16);
  const {
    onFileSelect,
    setUploaded,
    showToast
  } = t0;
  const [state, dispatch] = useReducer(fileUploaderReducer, initialFileUploaderState);
  let t1;
  if ($[0] !== onFileSelect || $[1] !== showToast || $[2] !== state.objectUrl) {
    t1 = file => {
      dispatch({
        type: "START_PROCESSING",
        payload: file
      });
      const result = validateFile(file);
      if (result.valid) {
        ;
        try {
          if (state.objectUrl) {
            URL.revokeObjectURL(state.objectUrl);
          }
          const timeoutId = window.setTimeout(() => {
            dispatch({
              type: "PROCESSING_TIMEOUT"
            });
          }, PROCESSING_TIMEOUT_MS);
          const newObjectUrl = URL.createObjectURL(file);
          window.clearTimeout(timeoutId);
          dispatch({
            type: "PROCESSING_SUCCESS",
            payload: {
              objectUrl: newObjectUrl,
              file
            }
          });
          if (file.type.startsWith("video/")) {
            setTimeout(() => {
              testVideoCompatibility(file).then(result_0 => {
                dispatch({
                  type: "SET_COMPATIBILITY_RESULT",
                  payload: result_0
                });
              });
            }, 100);
          }
          onFileSelect(file);
        } catch (t2) {
          const e = t2;
          console.error("Error processing file:", e);
          const errorMessage = e instanceof Error ? e.message : "Unexpected error processing file";
          dispatch({
            type: "PROCESSING_ERROR",
            payload: errorMessage
          });
          if (showToast) {
            showToast({
              type: "error" as any,
              message: errorMessage
            });
          }
        }
      } else {
        const errorMessage_0 = result.error ?? "Invalid file";
        dispatch({
          type: "PROCESSING_ERROR",
          payload: errorMessage_0
        });
        if (showToast) {
          showToast({
            type: "error" as any,
            message: errorMessage_0
          });
        }
      }
    };
    $[0] = onFileSelect;
    $[1] = showToast;
    $[2] = state.objectUrl;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const processFile = t1;
  let t2;
  if ($[4] !== processFile || $[5] !== state.processingFile) {
    t2 = () => {
      if (state.processingFile) {
        dispatch({
          type: "PROCESSING_RETRY"
        });
        processFile(state.processingFile);
      }
    };
    $[4] = processFile;
    $[5] = state.processingFile;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleRetry = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      dispatch({
        type: "RESET_STATE"
      });
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const resetState = t3;
  let t4;
  if ($[8] !== setUploaded || $[9] !== state.objectUrl) {
    t4 = e_0 => {
      e_0.stopPropagation();
      e_0.preventDefault();
      if (state.objectUrl) {
        URL.revokeObjectURL(state.objectUrl);
      }
      setUploaded(false);
      dispatch({
        type: "RESET_STATE"
      });
    };
    $[8] = setUploaded;
    $[9] = state.objectUrl;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleRemoveFile = t4;
  let t5;
  if ($[11] !== handleRemoveFile || $[12] !== handleRetry || $[13] !== processFile || $[14] !== state) {
    t5 = {
      state,
      processFile,
      handleRetry,
      resetState,
      handleRemoveFile,
      dispatch
    };
    $[11] = handleRemoveFile;
    $[12] = handleRetry;
    $[13] = processFile;
    $[14] = state;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export default useFileUploader;